import * as React from "react"
import { type ApolloError } from "@apollo/client/errors"
import { useSendMagicLinkEmailMutation } from "@digits-graphql/frontend/graphql-public"
import { logInErrorText } from "@digits-shared/helpers/logInErrors"
import { getMagicLinkScheme } from "src/frontend/components/Public/LogIn/getMagicLinkScheme"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import { useDisableFor } from "src/frontend/hooks/useDisableFor"

export function useSendMagicLink({
  redirectPath,
  onCompleted,
  onStart,
}: {
  redirectPath: string | undefined
  onCompleted?: () => void
  onStart?: () => void
}) {
  const { alertError } = useChromeAlerts()
  const {
    state: { emailAddress },
    dispatch,
  } = useOnboardingContext()
  const onSendMagicLinkCompleted = React.useCallback(() => {
    dispatch({ type: "SET_LOADING", loading: false })
    onCompleted?.()
  }, [dispatch, onCompleted])

  const onSendMagicLinkError = React.useCallback(
    (error: ApolloError) => {
      alertError(sessionError)
      dispatch({ type: "SET_ERROR_TEXT", text: logInErrorText(error) })
      dispatch({ type: "SET_LOADING", loading: false })
    },
    [dispatch, alertError]
  )

  const [sendMagicLinkEmail, { loading: magicLinkLoading }] = useSendMagicLinkEmailMutation({
    onCompleted: onSendMagicLinkCompleted,
    onError: onSendMagicLinkError,
    context: { publicAPI: true },
  })

  const onMagicLinkClick = React.useCallback(async () => {
    dispatch({ type: "SET_LOADING", loading: true })
    onStart?.()
    const { scheme, state } = await getMagicLinkScheme()
    return sendMagicLinkEmail({
      variables: {
        emailAddress: emailAddress || "",
        redirectPath,
        scheme,
        state,
      },
    })
  }, [dispatch, emailAddress, redirectPath, sendMagicLinkEmail, onStart])

  return React.useMemo(
    () => ({
      magicLinkLoading,
      onMagicLinkClick,
    }),
    [magicLinkLoading, onMagicLinkClick]
  )
}

export function useResendMagicLink() {
  const {
    state: { redirectPath },
  } = useOnboardingContext()
  const { disabled, disableForDuration } = useDisableFor(5000)
  const { alertSuccess } = useChromeAlerts()
  const onCompleted = React.useCallback(
    () => alertSuccess("Please check your email!"),
    [alertSuccess]
  )
  const { onMagicLinkClick, magicLinkLoading } = useSendMagicLink({
    redirectPath,
    onStart: disableForDuration,
    onCompleted,
  })

  return React.useMemo(
    () => ({ disabled, magicLinkLoading, onMagicLinkClick }),
    [disabled, magicLinkLoading, onMagicLinkClick]
  )
}
